import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import combinedCardData from '../common/Cardsdata';
import bankData from '../common/BankData';
import useSeo from '../hooks/useSeo';
import { AuthContext } from '../context/AuthContext';

const tabs = [
  'Reward Cards',
  'Travel Cards',
  'Fuel Cards',
  'Balance Transfer',
  'Business Cards',
  'Cashback Cards',
];

export default function CardTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCreditCardsPage = location.pathname === '/credit-cards';
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [navHeight, setNavHeight] = useState(0);
  const stickyRef = useRef(null);
  const [stickyHeight, setStickyHeight] = useState(0);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    setIsMobile(window.innerWidth <= 768);

    const selected = location.state?.selectedTab;
    if (selected && tabs.includes(selected)) setActiveTab(selected);
    if (selected && categories.includes(selected)) setCategoryFilter(selected);

    return () => window.removeEventListener('resize', handleResize);
  }, [location.state]);


  useEffect(() => {
    function calculateHeights() {
      try {
        const selectors = [
          '.navbar',
          'header',
          '#navbar',
          '.topbar',
          '.app-header',
          '.site-header',
        ];
        let navEl = selectors
          .map((s) => document.querySelector(s))
          .find(Boolean);

        if (!navEl) {
          const candidates = Array.from(
            document.querySelectorAll('body *')
          ).filter((el) => {
            const cs = getComputedStyle(el);
            return (
              (cs.position === 'fixed' || cs.position === 'sticky') &&
              Math.round(el.getBoundingClientRect().top) === 0 &&
              el.offsetHeight > 0
            );
          });
          navEl = candidates[0];
        }

        const computedNavHeight = navEl
          ? Math.ceil(navEl.getBoundingClientRect().height)
          : 0;
        setNavHeight(computedNavHeight);

        if (stickyRef.current) {
          setStickyHeight(
            Math.ceil(stickyRef.current.getBoundingClientRect().height)
          );
        } else {
          setStickyHeight(0);
        }
      } catch (err) {
        setNavHeight(0);
        setStickyHeight(0);
      }
    }

    calculateHeights();
    window.addEventListener('resize', calculateHeights);
    return () => window.removeEventListener('resize', calculateHeights);
  }, []);

  useSeo({
    title:
      'Compare Credit Cards in India | Rewards, Fees & Offers | CardAcademy',
    description:
      'Compare top credit cards by rewards, fees and benefits. Filter by bank and category to find the best card for you.',
    canonical: 'https://cardacademy.co/credit-cards',
  });

  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [bankFilter, setBankFilter] = useState('All Banks');
  const [expandedCard, setExpandedCard] = useState(null);
  const [feeFilter, setFeeFilter] = useState('All Fees');

  const categories = [
    'All',
    ...new Set(combinedCardData.map((card) => card.category)),
    'Lifestyle Cards',
  ];
  const allBanks = [
    'HDFC',
    'DBS',
    'HSBC',
    'ICICI',
    'IDFC',
    'Kotak',
    'YES Bank',
    'Standard Chartered',
    'SBI',
    'RBL',
    'Bank of Baroda',
    'Federal Bank',
    'Axis',
    'American Express',
    'AU Small Bank',
    'IndusInd Bank',
  ];
  const banks = ['All Banks', ...allBanks];

  const getBankLogo = (bankName) => {
    const bank = bankData.find((b) => b.name === bankName);
    return bank ? bank.logo : null;
  };

  const filteredCards = combinedCardData
    .filter((card) => {
      if (
        isCreditCardsPage &&
        categoryFilter !== 'All' &&
        categoryFilter !== 'Lifestyle Cards'
      )
        return card.category === categoryFilter;
      if (categoryFilter === 'Lifestyle Cards')
        return card.category === 'Lifestyle Cards';
      if (!isCreditCardsPage) return card.tab === activeTab;
      return true;
    })
    .filter(
      (card) =>
        bankFilter === 'All Banks' || (card.bank && card.bank === bankFilter)
    )
    .filter((card) => feeFilter === 'All Fees' || card.fee === feeFilter);

  const gradients = [
    'linear-gradient(216deg, #FFF4CE 1.83%, rgba(255, 255, 255, 0.50) 64.45%)',
    'linear-gradient(216deg, #F9DEDC 1.83%, rgba(255, 255, 255, 0.50) 64.45%)',
    'linear-gradient(216deg, #EADDFF 1.83%, rgba(255, 255, 255, 0.50) 64.45%)',
  ];

  const slugify = (text) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

  const handleCardClick = (card) => {
    const slug = slugify(card.creditcardname);
    navigate(`/credit-cards/${slug}`, { state: card });
  };

  // ✅ ADDED: replaces setPendingCard + setShowLoginModal
  const handleProtectedClick = (card) => {
    if (!user) {
      navigate('/login');
    } else {
      handleCardClick(card);
    }
  };

  const trendingCards = combinedCardData.filter((card) => card.trending);
  const filteredTrendingCards = trendingCards.filter((card) => {
    if (bankFilter === 'All Banks') return true;
    return card.bank && card.bank === bankFilter;
  });

  const displayedCards =
    !isCreditCardsPage && isMobile
      ? filteredTrendingCards
      : isCreditCardsPage
        ? filteredCards
        : filteredTrendingCards;

  const cardWidth = isMobile ? '92vw' : '310px';
  const mobileMinHeight = 580;
  const cardHeight = isMobile ? 'auto' : '640px';
  const imageBoxHeight = 200;
  const descBoxHeight = isMobile ? 140 : 150;

  return (
    <div
      className="card-tabs-container"
      style={{
        padding: isCreditCardsPage
          ? isMobile
            ? '2rem 1rem'
            : '2rem 1rem'
          : isMobile
            ? '2rem 1rem'
            : '6rem',
        backgroundColor: isCreditCardsPage ? '#f2f6ff' : '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {isCreditCardsPage ? (
        <>
          <h2
            style={{
              marginTop: isMobile ? '4rem' : '0',
              fontSize: isMobile ? '1.5rem' : '2.5rem',
              fontWeight: 700,
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            Compare Credit Cards
          </h2>
          <p
            style={{
              textAlign: 'center',
              color: '#555',
              fontSize: isMobile ? '1rem' : '1.2rem',
              marginBottom: '3rem',
              maxWidth: '800px',
            }}
          >
            Find the perfect credit card for your needs by comparing features,
            rewards, and fees.
          </p>
        </>
      ) : isMobile ? (
        <>
          <h2
            style={{
              fontSize: '1.6rem',
              fontWeight: 700,
              marginTop: '4rem',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            Choose the Perfect Credit Card
          </h2>
          <p
            style={{
              textAlign: 'center',
              color: '#555',
              fontSize: '1rem',
              marginBottom: '2rem',
              maxWidth: '350px',
            }}
          >
            Explore and compare trending credit cards that fit your lifestyle
            while maximizing rewards and savings.
          </p>
        </>
      ) : (
        <>
          <h2
            style={{
              fontSize: '2.5rem',
              fontWeight: 700,
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            Choose the Perfect Credit Card
          </h2>
          <p
            style={{
              textAlign: 'center',
              color: '#555',
              fontSize: '1.2rem',
              marginBottom: '3rem',
              maxWidth: '800px',
            }}
          >
            Explore and compare trending credit cards that fit your lifestyle
            while maximizing rewards and savings.
          </p>
        </>
      )}

      {isCreditCardsPage && isMobile ? (
        <>
          <div
            style={{
              position: 'sticky',
              top: `${navHeight}px`,
              zIndex: 999,
              width: '100vw',
              background: '#f2f6ff',
              padding: '0.6rem 0',
            }}
            ref={stickyRef}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '1300px',
                margin: '0 auto',
                boxSizing: 'border-box',
                padding: '0 1rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.6rem',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <label style={{ fontWeight: 600, marginBottom: '0.375rem' }}>
                    Category
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.55rem 0.9rem',
                      fontSize: '1rem',
                      borderRadius: '6px',
                      border: '1px solid #1E3FA6',
                      background: '#fff',
                    }}
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <label style={{ fontWeight: 600, marginBottom: '0.375rem' }}>
                    Banks
                  </label>
                  <select
                    value={bankFilter}
                    onChange={(e) => setBankFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.55rem 0.9rem',
                      fontSize: '1rem',
                      borderRadius: '6px',
                      border: '1px solid #1E3FA6',
                      background: '#fff',
                    }}
                  >
                    {banks.map((bank) => (
                      <option key={bank} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div style={{ height: `${stickyHeight}px`, width: '100%' }} />
        </>
      ) : (
        isCreditCardsPage && (
          <div
            style={{
              marginLeft: isMobile ? '4rem' : 0,
              display: 'flex',
              justifyContent: 'flex-start',
              gap: '2rem',
              flexWrap: 'wrap',
              width: '100%',
              maxWidth: '1300px',
              marginBottom: '3rem',
            }}
          >
            <div>
              <label
                style={{
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  display: 'block',
                }}
              >
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  padding: '0.6rem 1rem',
                  fontSize: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #1E3FA6',
                  background: '#fff',
                  minWidth: '200px',
                }}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  display: 'block',
                }}
              >
                Banks
              </label>
              <select
                value={bankFilter}
                onChange={(e) => setBankFilter(e.target.value)}
                style={{
                  padding: '0.6rem 1rem',
                  fontSize: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #1E3FA6',
                  background: '#fff',
                  minWidth: '200px',
                }}
              >
                {banks.map((bank) => (
                  <option key={bank} value={bank}>
                    {bank}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2rem',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '1400px',
        }}
      >
        {displayedCards.map((card, index) => {
          const isExpanded = expandedCard === card.id;
          const truncated = card.description.slice(0, 100);
          const gradientStyle = gradients[index % gradients.length];

          return (
            <div
              key={card.id}
              onClick={() => handleCardClick(card)}
              style={{
                width: cardWidth,
                height: cardHeight,
                minHeight: isMobile ? `${mobileMinHeight}px` : undefined,
                borderRadius: '16px',
                border: '1px solid white',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                marginBottom: '2rem',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'scale(1.02)')
              }
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'none')}
            >
              <div
                style={{
                  background: gradientStyle,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 0,
                }}
              />

              <div
                style={{
                  position: 'relative',
                  zIndex: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '1rem',
                    minHeight: '120px',
                    overflow: 'visible',
                  }}
                >
                  <div style={{ overflow: 'hidden' }}>
                    <h3
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        marginBottom: '0.35rem',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {card.creditcardname}
                    </h3>
                    <div
                      style={{
                        fontSize: '1rem',
                        color: '#777',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        lineHeight: 1.2,
                      }}
                    >
                      <span style={{ color: '#ffd700' }}>★</span>
                      <span>{card.rating}</span>
                    </div>
                  </div>
                  <img
                    src={getBankLogo(card.bank)}
                    alt={card.bank}
                    style={{
                      marginTop: 10,
                      width: 40,
                      height: 40,
                      objectFit: 'contain',
                    }}
                  />
                </div>

                <div
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    margin: '0 1rem',
                    marginBottom: '10px',
                  }}
                />

                {/* Image */}
                <div
                  style={{
                    height: `${imageBoxHeight}px`,
                    margin: '0 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={card.cardImage}
                    alt={card.bank}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      borderRadius: '10px',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </div>

                {/* Description */}
                <div
                  style={{
                    padding: '1rem',
                    fontSize: '0.9rem',
                    color: '#444',
                    minHeight: `${descBoxHeight}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ lineHeight: 1.55, overflow: 'hidden' }}>
                    {isExpanded ? null : `${truncated}... `}
                  </div>
                  {!isExpanded && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCard(card.id);
                        handleCardClick(card);
                      }}
                      style={{
                        color: '#1e3a8a',
                        fontWeight: 500,
                        cursor: 'pointer',
                        marginTop: '8px',
                        flexShrink: 0,
                      }}
                    >
                      Read More
                    </div>
                  )}
                </div>

                {/* Bottom */}
                <div
                  style={{
                    marginTop: 'auto',
                    paddingBottom: isMobile ? '0.25rem' : 0,
                  }}
                >
                  <div style={{ padding: '0 1rem 1.25rem' }}>
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: '#555',
                        marginBottom: '6px',
                      }}
                    >
                      Annual Fee
                    </p>
                    <p
                      style={{ fontWeight: 500, fontSize: '0.9rem', margin: 0 }}
                    >
                      {card.annualFee}
                    </p>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProtectedClick(card); // ✅ REPLACED: was setPendingCard + setShowLoginModal
                    }}
                    style={{
                      margin: '0 1rem 1rem',
                      padding: '1.1rem',
                      background: '#1e3a8a',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      width: 'calc(100% - 2rem)',
                      borderRadius: '10px',
                      alignSelf: 'center',
                      fontSize: isMobile ? '1.1rem' : undefined,
                    }}
                  >
                    {card.buttonText || 'Apply Now'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
